import { useRef } from 'react';
import { useIntl } from 'react-intl';
import { ArrowDown, ArrowUp, Plus, Trash2 } from 'lucide-react';
import Button from 'src/componentes/button';
import {
  MATRIX_COLUMN_LIMIT,
  MATRIX_ITEM_MAX_LENGTH,
  MATRIX_ROW_LIMIT,
  getMatrixInvalidItemIndexes,
  normalizeMatrixItems,
} from './FormularioGlobalQuestionTypes';

const FormularioGlobalQuestionMatrix = ({
  disabled,
  idPrefix,
  onChange,
  question,
  validationErrors,
}) => {
  const intl = useIntl();
  const questionRef = useRef(question);
  questionRef.current = question;
  const message = (id, values) => intl.formatMessage({ id }, values);

  const updateItems = (field, items) => {
    const nextQuestion = {
      ...questionRef.current,
      [field]: normalizeMatrixItems(items),
    };
    questionRef.current = nextQuestion;
    onChange(nextQuestion);
  };

  const moveItem = (field, index, direction) => {
    const items = [...questionRef.current[field]];
    const targetIndex = index + direction;
    if (targetIndex < 0 || targetIndex >= items.length) return;
    [items[index], items[targetIndex]] = [items[targetIndex], items[index]];
    updateItems(field, items);
  };

  return (
    <div className="tw:mt-4 tw:space-y-4">
      <p className="tw:m-0 tw:pb-3 tw:text-sm tw:text-slate-500">
        {message('msgMatrizSelecaoOrientacao')}
      </p>
      <fieldset className="tw:rounded-lg tw:border tw:border-slate-200 tw:p-3">
        <legend className="tw:float-none tw:mb-0 tw:w-auto tw:px-1 tw:text-sm tw:font-semibold">
          {message('labelSelecaoPorLinha')}
        </legend>
        <div className="tw:flex tw:flex-wrap tw:gap-x-5 tw:gap-y-2">
          {[false, true].map((multiple) => (
            <label
              className="tw:flex tw:cursor-pointer tw:items-center tw:gap-2 tw:text-sm"
              key={String(multiple)}
            >
              <input
                checked={
                  !!question.permitirMultiplaSelecaoPorLinha === multiple
                }
                className="tw:accent-primary"
                disabled={disabled}
                name={`${idPrefix}-selection`}
                onChange={() => {
                  const nextQuestion = {
                    ...questionRef.current,
                    permitirMultiplaSelecaoPorLinha: multiple,
                  };
                  questionRef.current = nextQuestion;
                  onChange(nextQuestion);
                }}
                type="radio"
                value={String(multiple)}
              />
              {message(
                multiple
                  ? 'labelMultiplasSelecoesPorLinha'
                  : 'labelUmaSelecaoPorLinha'
              )}
            </label>
          ))}
        </div>
      </fieldset>
      <div className="tw:grid tw:grid-cols-1 tw:gap-4 tw:pt-3 tw:xl:grid-cols-2">
        {['linhas', 'colunas'].map((field) => {
          const isRows = field === 'linhas';
          const items = question[field] || [];
          const limit = isRows ? MATRIX_ROW_LIMIT : MATRIX_COLUMN_LIMIT;
          const minimum = isRows ? 1 : 2;
          const errorId = `${idPrefix}-${field}-error`;
          const invalidItems = validationErrors?.[field]
            ? getMatrixInvalidItemIndexes(items)
            : new Set();

          return (
            <fieldset
              aria-describedby={validationErrors?.[field] ? errorId : undefined}
              className="tw:min-w-0 tw:rounded-lg tw:border tw:border-slate-200 tw:p-3"
              key={field}
            >
              <legend className="tw:float-none tw:mb-0 tw:w-auto tw:px-1 tw:text-sm tw:font-semibold">
                {message(isRows ? 'labelMatrizLinhas' : 'labelMatrizColunas')}
                <span className="tw:ml-2 tw:font-normal tw:text-slate-500">
                  {items.length}/{limit}
                </span>
              </legend>
              <div className="tw:space-y-2 tw:pt-3">
                {items.map((item, index) => {
                  const label = message(
                    isRows
                      ? 'labelMatrizLinhaNumero'
                      : 'labelMatrizColunaNumero',
                    { numero: index + 1 }
                  );
                  const inputId = `${idPrefix}-${field}-${item._formularioGlobalClientId}`;
                  const invalid = invalidItems.has(index);

                  return (
                    <div
                      className="tw:flex tw:flex-wrap tw:items-center tw:gap-1"
                      key={item._formularioGlobalClientId}
                    >
                      <label className="tw:sr-only" htmlFor={inputId}>
                        {label}
                      </label>
                      <input
                        aria-describedby={invalid ? errorId : undefined}
                        aria-invalid={invalid}
                        className="tw:h-9 tw:min-w-0 tw:flex-1 tw:rounded-md tw:border tw:border-slate-300 tw:bg-white tw:px-3 tw:text-sm tw:outline-none tw:focus-visible:border-primary tw:focus-visible:ring-2 tw:focus-visible:ring-primary/20 tw:disabled:opacity-50 tw:aria-invalid:border-red-500"
                        disabled={disabled}
                        id={inputId}
                        maxLength={MATRIX_ITEM_MAX_LENGTH}
                        onChange={(event) =>
                          updateItems(
                            field,
                            questionRef.current[field].map(
                              (current, currentIndex) =>
                                currentIndex === index
                                  ? { ...current, texto: event.target.value }
                                  : current
                            )
                          )
                        }
                        placeholder={label}
                        type="text"
                        value={item.texto}
                      />
                      <div className="tw:flex tw:items-center">
                        <Button
                          aria-label={`${message('labelMoverCima')}: ${label}`}
                          disabled={disabled || index === 0}
                          onClick={() => moveItem(field, index, -1)}
                          size="icon-sm"
                          type="button"
                          color="default"
                        >
                          <ArrowUp />
                        </Button>
                        <Button
                          aria-label={`${message('labelMoverBaixo')}: ${label}`}
                          disabled={disabled || index === items.length - 1}
                          onClick={() => moveItem(field, index, 1)}
                          size="icon-sm"
                          type="button"
                          color="default"
                        >
                          <ArrowDown />
                        </Button>
                        <Button
                          aria-label={`${message('excluir')}: ${label}`}
                          disabled={disabled || items.length <= minimum}
                          onClick={() =>
                            updateItems(
                              field,
                              questionRef.current[field].filter(
                                (_, currentIndex) => currentIndex !== index
                              )
                            )
                          }
                          size="icon-sm"
                          type="button"
                          color="default"
                        >
                          <Trash2 />
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
              {validationErrors?.[field] && (
                <p
                  className="tw:mb-0 tw:mt-2 tw:text-sm tw:text-red-600"
                  id={errorId}
                  role="alert"
                >
                  {message(validationErrors[field])}
                </p>
              )}
              <Button
                className="tw:mt-3"
                disabled={disabled || items.length >= limit}
                onClick={() =>
                  updateItems(field, [
                    ...questionRef.current[field],
                    { texto: '' },
                  ])
                }
                size="sm"
                type="button"
                color="outline-secondary"
              >
                <Plus />
                {message(
                  isRows
                    ? 'labelAdicionarLinhaMatriz'
                    : 'labelAdicionarColunaMatriz'
                )}
              </Button>
            </fieldset>
          );
        })}
      </div>
    </div>
  );
};

export default FormularioGlobalQuestionMatrix;
